/**
 * When in the day things happen.
 *
 * This is the analysis ADR-0006 exists to make possible. An instant alone
 * cannot answer it: the wall-clock reading has to have been recorded at
 * capture, because recomputing it later in whatever zone the device is in now
 * moves every entry by an hour after a clock change and by rather more after a
 * holiday. `minutesSinceMidnight` reads the clock that was actually on the wall.
 */

import { minutesSinceMidnight } from '../model/timestamp.js'
import type { SeriesPoint, Series } from './series.js'

const MINUTES_PER_DAY = 1_440

export interface TimeOfDayBucket {
  /** Minutes past local midnight. */
  readonly startMinute: number
  /** Exclusive. */
  readonly endMinute: number
  /** `09:00–10:00`. */
  readonly label: string
  readonly count: number
}

export interface QuantitativeTimeOfDayBucket extends TimeOfDayBucket {
  readonly mean: number
}

export interface QuantitativeTimeOfDay {
  readonly kind: 'quantitative'
  readonly bucketMinutes: number
  readonly buckets: readonly QuantitativeTimeOfDayBucket[]
}

export interface CategoricalTimeOfDay {
  readonly kind: 'categorical'
  readonly bucketMinutes: number
  readonly buckets: readonly TimeOfDayBucket[]
}

export type TimeOfDayDistribution = QuantitativeTimeOfDay | CategoricalTimeOfDay

export interface TimeOfDayOptions {
  /** Must divide evenly into a day. Defaults to hourly. */
  readonly bucketMinutes?: number
}

function clockLabel(minutes: number): string {
  const hour = Math.floor(minutes / 60) % 24
  const minute = minutes % 60
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

function bucketIndexOf(point: SeriesPoint, width: number): number {
  return Math.floor(minutesSinceMidnight(point.at) / width)
}

/**
 * Counts by time of day, and for a quantitative track the mean in each.
 *
 * Only periods with measurements appear. A 03:00 bucket showing zero and a
 * 03:00 bucket that does not exist look identical on a bar chart, but only one
 * of them is a claim about what was measured.
 */
export function timeOfDayDistribution(
  series: Series,
  options: TimeOfDayOptions = {},
): TimeOfDayDistribution {
  const width = options.bucketMinutes ?? 60
  const bucketMinutes = width > 0 && width <= MINUTES_PER_DAY ? width : 60

  const label = (index: number) =>
    `${clockLabel(index * bucketMinutes)}–${clockLabel((index + 1) * bucketMinutes)}`

  if (series.kind === 'categorical') {
    const counts = new Map<number, number>()
    for (const point of series.points) {
      const index = bucketIndexOf(point, bucketMinutes)
      counts.set(index, (counts.get(index) ?? 0) + 1)
    }
    return {
      kind: 'categorical',
      bucketMinutes,
      buckets: [...counts.entries()]
        .sort(([a], [b]) => a - b)
        .map(([index, count]) => ({
          startMinute: index * bucketMinutes,
          endMinute: (index + 1) * bucketMinutes,
          label: label(index),
          count,
        })),
    }
  }

  const groups = new Map<number, number[]>()
  for (const point of series.points) {
    const index = bucketIndexOf(point, bucketMinutes)
    const existing = groups.get(index)
    if (existing === undefined) {
      groups.set(index, [point.quantity])
    } else {
      existing.push(point.quantity)
    }
  }

  return {
    kind: 'quantitative',
    bucketMinutes,
    buckets: [...groups.entries()]
      .sort(([a], [b]) => a - b)
      .map(([index, quantities]) => ({
        startMinute: index * bucketMinutes,
        endMinute: (index + 1) * bucketMinutes,
        label: label(index),
        count: quantities.length,
        mean: quantities.reduce((total, value) => total + value, 0) / quantities.length,
      })),
  }
}
